import React from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { TableRow, TableCell } from "@/components/table";
import { useQueryGetExamPaper } from "@/apis/exam_papers/getExamPaper";
import { getPaperTypeLabel } from "@/constants/exam";

interface ExamPaperItemProps {
  index: number;
  examPaperId: string;
  onRemove: () => void;
  isLastItem: boolean;
}

const ExamPaperItem: React.FC<ExamPaperItemProps> = ({
  index,
  examPaperId,
  onRemove,
  isLastItem,
}) => {
  const { data: examPaper, isLoading } = useQueryGetExamPaper(examPaperId);

  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={4} className="text-center py-4 text-zinc-500">
          Loading...
        </TableCell>
      </TableRow>
    );
  }

  if (!examPaper) {
    return (
      <TableRow>
        <TableCell colSpan={4} className="text-center py-4 text-red-500">
          Exam paper not found
        </TableCell>
      </TableRow>
    );
  }

  const isSAT = examPaper.exam_type === "SAT";

  return (
    <TableRow className="hover:bg-gray-50">
      <TableCell>
        <div className="font-medium text-gray-900">{examPaper.title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {examPaper.exam_type}
        </div>
      </TableCell>
      <TableCell>
        <Badge color="zinc" className="text-xs">
          {getPaperTypeLabel(examPaper.subject)}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="space-y-2">
          {examPaper.sections?.map((section, sectionIndex) => {
            const sectionLabel = isSAT
              ? `M${sectionIndex + 1}`
              : `${sectionIndex + 1}`;
            const questionCount = section.question_ids?.length || 0;
            const duration = section.duration || 0;
            const showBadge = (examPaper.sections?.length || 0) > 1;

            return (
              <div key={sectionIndex} className="flex items-center gap-2">
                {showBadge && (
                  <Badge color="zinc" className="text-xs">
                    {sectionLabel}
                  </Badge>
                )}
                <div className="text-xs text-zinc-500">
                  {questionCount}Q • {duration}min
                </div>
              </div>
            );
          }) || <div className="text-xs text-zinc-400">No sections</div>}
        </div>
      </TableCell>
      <TableCell>
        <Button
          type="button"
          onClick={onRemove}
          disabled={isLastItem}
          plain
          className={`p-2 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors ${
            isLastItem ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }`}
          aria-label="Remove exam paper"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default ExamPaperItem;
